import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  Modal,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { commonStyles } from '../../common/styles';
import { MainDialogMobile, MainDialogTv } from '../../components/main_dialog';
import AppColors from '../../common/colors';
import Constants from '../../utils/constants';
import SplashLogo from '../../../assets/svg/logo.svg';
import localization from '../../common/localization';
import { persistedStorage } from '../../common/storage';
import useGlobalStyles from '../../common/style_context';
import useScale from '../../common/hooks/useScale';
import { MainStackParams } from '../../common/screen_params';
import States from '../../common/states';
import { AccountProvider } from '../../data';
import { ApiError } from '../../types/errors';
import Device from '../../common/device';
import { toJS } from 'mobx';
import Updater from '../../common/updater';
import sendRequest from '../../data/response';
import DeviceInfo from 'react-native-device-info';
import * as Sentry from '@sentry/react-native';
import Utils from '../../utils/utils';
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

const mb = 1024 * 1024;

type Props = {
  isTv: boolean;
  onLoaded?: (screen: keyof MainStackParams) => void;
};

const Splash = memo<Props>(({ isTv, onLoaded }) => {
  const [attempt, setAttempt] = useState<number>();

  const [delay, setDelay] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [update, setUpdate] = useState(false);

  const [error, setError] = useState<string>();

  const [updateMessage, setUpdateMessage] = useState<string>();
  const updateUrl = useRef<string>(undefined);
  const updateVersion = useRef<string>(undefined);
  const [updateLoading, setUpdateLoading] = useState<number>();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDelay(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const load = useCallback(async () => {
    setError(undefined);
    const sid = persistedStorage.getString('ssid');
    if (sid) {
      try {
        const sidName = persistedStorage.getString('ssidName');
        await States.mainState.initSession(sid, sidName);
        try {
          await AccountProvider.setStandart('hls_h264');
          if (States.mainState._settings)
            States.mainState._settings.stream_standard.value = 'hls_h264';
        } catch {}
        setLoaded(true);
      } catch (err: any) {
        console.log(err);

        const error = Utils.getTvErrorMessage(err);
        const tryAgain =
          err instanceof ApiError
            ? !Constants.criticalErrors.includes(err.code)
            : true;
        setAttempt(p => (tryAgain ? p ?? 0 : undefined));
        setError(error);
      }
    } else setLoaded(true);
  }, []);

  /** Загрузка настроек из хранилища и запрос данных аккаунта */
  useEffect(() => {
    Sentry.setTag('is_tv', isTv);

    if (Platform.OS === 'android' && isTv) {
      let scale = persistedStorage.getNumber('overrideScale');
      if (scale === undefined) {
        const dim = Dimensions.get('window');
        if (dim.scale === 1) {
          persistedStorage.set('overrideScale', 1.5);
          scale = 1.5;
        }
      }
      if (scale !== undefined)
        Dimensions.set({
          screen: Dimensions.get('screen'),
          window: { ...Dimensions.get('window'), scale },
        });
    }

    const lang =
      persistedStorage.getString('forceLang') ??
      localization.getInterfaceLanguage();
    localization.setLanguage(lang);

    load();
  }, []);

  /** Проверка обновлений */
  useEffect(() => {
    (async () => {
      if (!(await Updater.isUpdatable())) {
        setUpdate(true);
        return;
      }

      const openDialog = async (json: any) => {
        const abis = await DeviceInfo.supportedAbis();
        const url = json[abis.find(e => e in json) ?? 'url'];
        updateUrl.current = url;
        updateVersion.current = json.version;

        const granted = await Updater.permissionGranted();
        const text = `${localization.settings.formatString(
          localization.settings.updateQuestion,
          json.version.replace('+', '.'),
        )}\n`;
        const text2 = granted
          ? localization.settings.updateRecommended
          : `${localization.settings.updateNeedPermissions}\n` +
            localization.settings.updateNeedPermissions2;
        setUpdateMessage(`${text}\n${text2}`);
      };

      try {
        const res = await sendRequest(
          'https://webmedia.idc.md/android/version.json',
          { 'Cache-Control': 'no-cache' },
        );

        const json = res[0];
        if (!/^\d+.\d+.\d+\+\d+$/.test(json.version)) throw undefined;

        const strs: string[] = json.version.split('+');
        const build = +strs[1];
        if (build > +Device.buildNumber()) {
          openDialog(json);
          return;
        }

        const [major, minor, revision] = strs[0].split('.').map(e => +e);
        const [maj, min, rev] = Device.version()
          .split('.')
          .map(e => +e);
        if (major > maj || minor > min || revision > rev) {
          openDialog(json);
          return;
        }
        Updater.removeDownloaded(() => setUpdate(true));
      } catch {
        setUpdate(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!delay || !loaded || !update || error !== undefined) return;

    const session = toJS(States.mainState.session);
    if (isTv) {
      if (session) {
        //TODO handle last screen
        if (persistedStorage.getNumber('startScreen') === 1) onLoaded?.('Tv');
        else onLoaded?.('MainTv');
      } else onLoaded?.('LoginTv');
    } else {
      if (session) onLoaded?.('MainMobile');
      else {
        onLoaded?.('LoginMobile');
      }
    }
  }, [delay, loaded, update]);

  const cancel = useCallback(() => {
    persistedStorage.delete('ssid');
    persistedStorage.delete('ssidName');
    setLoaded(true);
    setError(undefined);
  }, []);

  const submitDialog = useCallback(
    (action?: string) => {
      switch (action) {
        case 'tryAgain':
          setAttempt(p => (p !== undefined ? p + 1 : p));
          load?.();
          break;
        case 'update':
          setUpdateMessage(undefined);
          (async () => {
            if (!(await Updater.openSettingsIfNotAllowed())) {
              setTimeout(() => {
                setUpdate(true);
              }, 300);
              return;
            }
            await Updater.install(
              updateVersion.current!,
              updateUrl.current!,
              (p, t) => {
                setUpdateLoading(p / mb);
                setTotal(t / mb);
              },
              () => {
                setUpdateLoading(undefined);
                setUpdate(true);
              },
            );
          })();
          break;
        case 'skip':
          setUpdateMessage(undefined);
          Updater.removeDownloaded().then(() => setUpdate(true));
          break;
        default:
          cancel?.();
          break;
      }
    },
    [load, cancel],
  );

  const version = useMemo(
    () => `IDC IPTV ${Device.version()}.${Device.buildNumber()}`,
    [],
  );

  const errorActions = useMemo(() => {
    if (attempt !== undefined) {
      if (attempt >= 3) {
        return [
          { text: localization.general.tryAgain, action: 'tryAgain' },
          { text: localization.general.cancel, action: 'cancel' },
        ];
      }
      return [{ text: localization.general.tryAgain, action: 'tryAgain' }];
    }
    return [{ text: localization.general.ok, action: 'cancel' }];
  }, [attempt]);
  const updateActions = useMemo(() => {
    return [
      { text: localization.general.yes, action: 'update' },
      { text: localization.general.no, action: 'skip' },
    ];
  }, []);

  return (
    <SafeAreaProvider style={styles.container}>
      {isTv !== undefined &&
        (isTv ? (
          <TvSplash
            error={error}
            errorActions={errorActions}
            version={version}
            updateMessage={updateMessage}
            updateActions={updateActions}
            updateCurrent={updateLoading}
            updateTotal={total}
            submitDialog={submitDialog}
          />
        ) : (
          <MobileSplash
            error={error}
            errorActions={errorActions}
            version={version}
            updateMessage={updateMessage}
            updateActions={updateActions}
            updateCurrent={updateLoading}
            updateTotal={total}
            submitDialog={submitDialog}
          />
        ))}
    </SafeAreaProvider>
  );
});
Splash.displayName = 'Splash';

const tvStyles = [
  'fs=36,fw=500,c=white',
  `ac,w=50%,bg=${AppColors.containerGray},p=16,br=6`,
  `h=5,bg=${AppColors.mainBlue}`,
  'fs=36,fw=700,c=white,mb=100',
  'fs=36,fw=400,c=white,mb=50,tc',
];

type WrappedProps = {
  error?: string;
  errorActions: { text: string; action?: string }[];
  version: string;
  updateMessage?: string;
  updateActions: { text: string; action?: string }[];
  updateCurrent?: number;
  updateTotal: number;
  submitDialog: (action?: string) => void;
};

const TvSplash = memo<WrappedProps>(
  ({
    error,
    errorActions,
    version,
    updateMessage,
    updateActions,
    updateCurrent,
    updateTotal,
    submitDialog,
  }) => {
    const [textStyle, updateDialog, progressStyle, updateTitle, updateText] =
      useGlobalStyles(tvStyles);

    const [scale] = useScale();
    const container = useMemo(
      () =>
        StyleSheet.compose(styles.tvCenter, {
          paddingBottom: Constants.tvSafeArea.bottom / scale,
        }),
      [scale],
    );

    const progress = useMemo(
      () =>
        StyleSheet.compose(progressStyle, {
          width: `${((updateCurrent ?? 0) / updateTotal) * 100}%`,
        }),
      [updateCurrent, updateTotal],
    );

    const modal = useMemo(() => {
      if (updateMessage !== undefined)
        return (
          <MainDialogTv
            title={localization.settings.update}
            text={updateMessage}
            actions={updateActions}
            submit={submitDialog}
          />
        );
      if (error !== undefined && updateCurrent === undefined)
        return (
          <MainDialogTv
            title={localization.error.error}
            text={error}
            actions={errorActions}
            submit={submitDialog}
          />
        );
      return undefined;
    }, [error, updateMessage, updateCurrent]);

    return (
      <View style={styles.tvContainer}>
        <View style={commonStyles.f1} />
        <View style={container}>
          <View style={styles.tvLogo}>
            <SplashLogo />
          </View>
          <Text style={textStyle}>{version}</Text>
        </View>
        <View style={commonStyles.f1} />
        {updateCurrent !== undefined && (
          <View style={styles.updateCover}>
            <View style={updateDialog}>
              <Text style={updateTitle}>{localization.settings.update}</Text>
              <Text style={updateText}>
                {localization.settings.updateProcess}
              </Text>
              <Text style={updateText}>
                {localization.general.download}... {updateCurrent.toFixed(1)}{' '}
                {localization.general.mb} / {updateTotal.toFixed(1)}{' '}
                {localization.general.mb}
              </Text>
              <View style={styles.updateProgress}>
                <View style={progress} />
              </View>
            </View>
          </View>
        )}
        <Modal transparent visible={modal !== undefined}>
          {modal}
        </Modal>
      </View>
    );
  },
);
TvSplash.displayName = 'SplashTv';

const MobileSplash = memo<WrappedProps>(
  ({
    error,
    errorActions,
    version,
    updateMessage,
    updateActions,
    updateCurrent,
    updateTotal,
    submitDialog,
  }) => {
    const { bottom } = useSafeAreaInsets();

    const style = useMemo(
      () => StyleSheet.compose(commonStyles.f1, { paddingBottom: bottom }),
      [bottom],
    );
    const progress = useMemo(
      () =>
        StyleSheet.compose(styles.progressMobile, {
          width: `${((updateCurrent ?? 0) / updateTotal) * 100}%`,
        }),
      [updateCurrent, updateTotal],
    );

    return (
      <View style={style}>
        <View style={styles.mobileLogo}>
          <SplashLogo />
        </View>
        <Text style={styles.mobileText}>{version}</Text>
        {updateCurrent !== undefined && (
          <View style={styles.updateCover}>
            <View style={styles.updateDialogMobile}>
              <Text style={styles.updateTitleMobile}>
                {localization.settings.update}
              </Text>
              <Text style={styles.updateTextMobile}>
                {localization.settings.updateProcess}
              </Text>
              <Text style={styles.updateTextMobile}>
                {localization.general.download}... {updateCurrent.toFixed(1)}{' '}
                {localization.general.mb} / {updateTotal.toFixed(1)}{' '}
                {localization.general.mb}
              </Text>
              <View style={styles.updateProgress}>
                <View style={progress} />
              </View>
            </View>
          </View>
        )}
        <Modal transparent visible={error !== undefined}>
          <MainDialogMobile
            title={localization.error.error}
            text={error ?? ''}
            actions={errorActions}
            submit={submitDialog}
          />
        </Modal>
        <Modal transparent visible={updateMessage !== undefined}>
          <MainDialogMobile
            title={localization.settings.update}
            text={updateMessage ?? ''}
            actions={updateActions}
            submit={submitDialog}
          />
        </Modal>
      </View>
    );
  },
);
MobileSplash.displayName = 'SplashMobile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: AppColors.mainBlack,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tvContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  tvCenter: {
    flex: 1,
    alignItems: 'center',
  },
  tvLogo: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileLogo: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  mobileText: {
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
  },
  updateCover: {
    ...commonStyles.centeredFill,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  updateDialogMobile: {
    backgroundColor: AppColors.containerGray,
    width: '90%',
    alignItems: 'center',
    padding: 6,
    borderRadius: 6,
  },
  updateProgress: {
    alignSelf: 'stretch',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  updateTitleMobile: {
    ...commonStyles.text,
    fontSize: 24,
    fontWeight: '700',
    color: 'white',
    marginBottom: 50,
    textAlign: 'center',
  },
  updateTextMobile: {
    ...commonStyles.text,
    fontSize: 24,
    color: 'white',
    marginBottom: 30,
    textAlign: 'center',
  },
  progressMobile: {
    backgroundColor: AppColors.mainBlue,
    height: 5,
  },
});

export default Splash;
